import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { experienceStats, getCurrentExperiences } from '../../data/experienceData';

export function ExperienceHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const currentPositions = getCurrentExperiences();

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        gsap.fromTo(
          '.hero-animate',
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power2.out',
          }
        );
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative pt-32 pb-16 px-8 md:px-12 lg:px-24">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Label */}
        <span className="hero-animate text-xs text-white/30 tracking-[0.3em] uppercase">
          Werdegang
        </span>

        {/* Title */}
        <h1 className="hero-animate mt-6 text-5xl md:text-7xl lg:text-8xl font-light text-white leading-tight">
          Meine
          <br />
          <span className="text-emerald-500">Erfahrung</span>
        </h1>

        {/* Subtitle */}
        <p className="hero-animate mt-8 text-xl text-white/50 max-w-2xl">
          Über {experienceStats.totalYears} Jahre Erfahrung in der Webentwicklung und UX/UI Design.
          Von DAX-Konzernen bis zu innovativen Startups.
        </p>

        {/* Current Positions Badge */}
        <div className="hero-animate mt-8 flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
          <span className="text-sm text-white/60">
            Aktuell {currentPositions.length} aktive {currentPositions.length === 1 ? 'Position' : 'Positionen'}
          </span>
        </div>

        {/* Stats */}
        <div className="hero-animate mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center md:text-left">
            <span className="text-4xl md:text-5xl font-light text-white">
              {experienceStats.totalYears}+
            </span>
            <p className="mt-2 text-sm text-white/40 uppercase tracking-wider">Jahre</p>
          </div>
          <div className="text-center md:text-left">
            <span className="text-4xl md:text-5xl font-light text-white">
              {experienceStats.totalProjects}+
            </span>
            <p className="mt-2 text-sm text-white/40 uppercase tracking-wider">Projekte</p>
          </div>
          <div className="text-center md:text-left">
            <span className="text-4xl md:text-5xl font-light text-white">
              {experienceStats.industries}
            </span>
            <p className="mt-2 text-sm text-white/40 uppercase tracking-wider">Branchen</p>
          </div>
          <div className="text-center md:text-left">
            <span className="text-4xl md:text-5xl font-light text-white">
              {experienceStats.technologies}+
            </span>
            <p className="mt-2 text-sm text-white/40 uppercase tracking-wider">Technologien</p>
          </div>
        </div>
      </div>
    </section>
  );
}
